import React, { useEffect, useMemo, useState } from 'react';
import { SearchBar } from '../ui';
import { locationQrApi } from './locationQrApi';
import { getSiteLabelFromList } from './qrFacilityData';
import { QR_AREA_TYPES } from './qrFormData';
import { buildLocationQrSelections } from './qrSelections';

const INPUT_CLASS =
  'w-full rounded-lg border border-white/40 bg-white/15 backdrop-blur-md px-4 py-2.5 text-sm text-gray-900 font-["Open_Sans"] focus:outline-none focus:ring-2 focus:ring-green-500/40 focus:border-green-500/60';

const LABEL_CLASS =
  'block text-[11px] font-bold uppercase tracking-wider text-gray-500 mb-1.5 font-["Open_Sans"]';

const CHECKBOX_CLASS =
  'h-4 w-4 rounded border-gray-300 text-green-600 focus:ring-green-500 focus:ring-offset-0';

const SelectAllControl = ({ checked, onChange, disabled, label = 'Select all' }) => (
  <label className="inline-flex items-center gap-2 cursor-pointer select-none shrink-0">
    <input
      type="checkbox"
      checked={checked}
      onChange={onChange}
      disabled={disabled}
      className={CHECKBOX_CLASS}
    />
    <span className="text-[11px] font-bold uppercase tracking-wider text-gray-600 font-['Open_Sans']">
      {label}
    </span>
  </label>
);

const CheckboxRow = ({ checked, onChange, disabled, label }) => (
  <label
    className={`flex items-center gap-3 rounded-lg border px-3 py-2.5 cursor-pointer transition-colors font-['Open_Sans'] ${
      checked
        ? 'border-green-500/70 bg-green-500/20 backdrop-blur-sm'
        : 'border-white/40 bg-white/10 backdrop-blur-sm hover:border-green-400/50 hover:bg-white/15'
    }`}
  >
    <input
      type="checkbox"
      checked={checked}
      onChange={onChange}
      disabled={disabled}
      className={CHECKBOX_CLASS}
    />
    <span className="text-xs font-semibold uppercase tracking-wide text-gray-800">
      {label}
    </span>
  </label>
);

const SummaryRow = ({ label, value, highlight }) => (
  <div>
    <dt className="text-[11px] font-bold uppercase tracking-wider text-gray-500">
      {label}
    </dt>
    <dd
      className={`mt-1 text-sm font-semibold break-words ${
        highlight ? 'text-green-700 text-xl font-bold' : 'text-gray-900'
      }`}
    >
      {value}
    </dd>
  </div>
);

const SearchCheckboxList = ({
  searchId,
  searchLabel,
  searchValue,
  onSearchChange,
  items,
  selectedIds,
  onToggle,
  onToggleAll,
  allSelected,
  isSaving,
  noResultsMessage,
  columns = 2,
}) => (
  <div className="flex flex-col gap-4">
    <div className="flex flex-col sm:flex-row sm:items-end gap-3 sm:justify-between">
      <div className="flex-1 min-w-0">
        <label htmlFor={searchId} className={LABEL_CLASS}>
          {searchLabel}
        </label>
        <SearchBar
          id={searchId}
          placeholder={searchLabel}
          value={searchValue}
          onChange={e => onSearchChange(e.target.value)}
          disabled={isSaving}
          iconType="search"
          className="!w-full !h-12 !bg-white/15 !shadow-none !outline-white/30"
        />
      </div>
      <SelectAllControl
        checked={allSelected}
        onChange={onToggleAll}
        disabled={isSaving || items.length === 0}
      />
    </div>
    {items.length === 0 ? (
      <p className="text-sm text-gray-500 py-2">{noResultsMessage}</p>
    ) : (
      <div
        className={
          columns === 2
            ? 'grid grid-cols-1 sm:grid-cols-2 gap-2'
            : 'flex flex-col gap-2'
        }
      >
        {items.map(item => (
          <CheckboxRow
            key={item.id}
            label={item.name}
            checked={selectedIds.includes(item.id)}
            onChange={() => onToggle(item.id)}
            disabled={isSaving}
          />
        ))}
      </div>
    )}
  </div>
);

const CreateMultipleQR = ({
  sites = [],
  siteId: controlledSiteId,
  siteAreas,
  areasLoading,
  onSiteChange,
  onGenerate,
  onCancel,
  isSaving,
  error,
}) => {
  const [siteId, setSiteId] = useState(
    controlledSiteId || sites[0]?.site_id || ''
  );
  const [areaType, setAreaType] = useState('blocks');
  const [selectedBlockIds, setSelectedBlockIds] = useState([]);
  const [selectedFloors, setSelectedFloors] = useState({});
  const [selectedItemIds, setSelectedItemIds] = useState([]);
  const [amenitySearch, setAmenitySearch] = useState('');
  const [streetSearch, setStreetSearch] = useState('');
  const [defaultLandmark, setDefaultLandmark] = useState('');
  const [preview, setPreview] = useState(null);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [previewError, setPreviewError] = useState('');

  const blocks = siteAreas?.blocks ?? [];
  const amenities = siteAreas?.amenities ?? [];
  const streets = siteAreas?.streets ?? [];

  useEffect(() => {
    if (controlledSiteId && controlledSiteId !== siteId) {
      setSiteId(controlledSiteId);
    } else if (!siteId && sites[0]?.site_id) {
      setSiteId(sites[0].site_id);
    }
  }, [controlledSiteId, sites, siteId]);

  const filteredAmenities = useMemo(() => {
    const q = amenitySearch.trim().toLowerCase();
    if (!q) return amenities;
    return amenities.filter(a => a.name.toLowerCase().includes(q));
  }, [amenities, amenitySearch]);

  const filteredStreets = useMemo(() => {
    const q = streetSearch.trim().toLowerCase();
    if (!q) return streets;
    return streets.filter(s => s.name.toLowerCase().includes(q));
  }, [streets, streetSearch]);

  const selections = useMemo(
    () =>
      buildLocationQrSelections(
        areaType,
        selectedBlockIds,
        selectedFloors,
        selectedItemIds,
        { streets }
      ),
    [areaType, selectedBlockIds, selectedFloors, selectedItemIds, streets]
  );

  useEffect(() => {
    if (!siteId || selections.length === 0) {
      setPreview(null);
      setPreviewError('');
      return undefined;
    }

    let cancelled = false;
    const timer = setTimeout(async () => {
      setPreviewLoading(true);
      setPreviewError('');
      try {
        const data = await locationQrApi.previewCount({
          site_id: siteId,
          selections,
        });
        if (!cancelled) setPreview(data);
      } catch (err) {
        if (!cancelled) {
          setPreview(null);
          setPreviewError(err.message || 'Could not load preview counts');
        }
      } finally {
        if (!cancelled) setPreviewLoading(false);
      }
    }, 400);

    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
  }, [siteId, selections, areaType]);

  const resetSelections = () => {
    setSelectedBlockIds([]);
    setSelectedFloors({});
    setSelectedItemIds([]);
    setAmenitySearch('');
    setStreetSearch('');
    setPreview(null);
    setPreviewError('');
  };

  const handleSiteChange = e => {
    const nextSiteId = e.target.value;
    setSiteId(nextSiteId);
    onSiteChange?.(nextSiteId);
    resetSelections();
  };

  const handleAreaTypeChange = type => {
    setAreaType(type);
    resetSelections();
  };

  const toggleBlock = blockId => {
    setSelectedBlockIds(prev => {
      if (prev.includes(blockId)) {
        setSelectedFloors(floors => {
          const next = { ...floors };
          delete next[blockId];
          return next;
        });
        return prev.filter(id => id !== blockId);
      }
      return [...prev, blockId];
    });
  };

  const allBlocksSelected =
    blocks.length > 0 && selectedBlockIds.length === blocks.length;

  const toggleAllBlocks = () => {
    if (allBlocksSelected) {
      setSelectedBlockIds([]);
      setSelectedFloors({});
      return;
    }
    setSelectedBlockIds(blocks.map(b => b.id));
  };

  const toggleFloor = (blockId, floorNo) => {
    setSelectedFloors(prev => {
      const current = prev[blockId] || [];
      const next = current.includes(floorNo)
        ? current.filter(n => n !== floorNo)
        : [...current, floorNo];
      return { ...prev, [blockId]: next };
    });
  };

  const allFloorsSelectedForBlock = block => {
    const floors = block.floors ?? [];
    const selected = selectedFloors[block.id] || [];
    return floors.length > 0 && selected.length === floors.length;
  };

  const toggleAllFloorsForBlock = block => {
    setSelectedFloors(prev => {
      if (allFloorsSelectedForBlock(block)) {
        return { ...prev, [block.id]: [] };
      }
      return {
        ...prev,
        [block.id]: (block.floors ?? []).map(f => f.floor_no),
      };
    });
  };

  const allFilteredAmenitiesSelected =
    filteredAmenities.length > 0 &&
    filteredAmenities.every(a => selectedItemIds.includes(a.id));

  const allFilteredStreetsSelected =
    filteredStreets.length > 0 &&
    filteredStreets.every(s => selectedItemIds.includes(s.id));

  const toggleItem = itemId => {
    setSelectedItemIds(prev =>
      prev.includes(itemId) ? prev.filter(id => id !== itemId) : [...prev, itemId]
    );
  };

  const toggleAllFiltered = (filtered, allSelected) => {
    if (allSelected) {
      const filteredIds = new Set(filtered.map(i => i.id));
      setSelectedItemIds(prev => prev.filter(id => !filteredIds.has(id)));
      return;
    }
    const filteredIds = filtered.map(i => i.id);
    setSelectedItemIds(prev => [...new Set([...prev, ...filteredIds])]);
  };

  const summary = useMemo(() => {
    const siteName = getSiteLabelFromList(sites, siteId).toUpperCase();

    if (areaType === 'blocks') {
      const selectedBlocks = blocks.filter(b => selectedBlockIds.includes(b.id));
      const floorCount = selectedBlocks.reduce(
        (sum, block) => sum + (selectedFloors[block.id]?.length || 0),
        0
      );

      return {
        siteName,
        areaLabel: 'BLOCKS',
        selectionLabel: 'Selected blocks',
        selectionLine:
          selectedBlocks.length > 0
            ? `${selectedBlocks.length} (${selectedBlocks.map(b => b.name).join(', ')})`
            : '0',
        detailLabel: 'Floors',
        detailLine: String(floorCount),
        total: preview?.total ?? floorCount,
        toCreate: preview?.to_create,
        alreadyExists: preview?.already_exists,
      };
    }

    if (areaType === 'amenities') {
      const count = amenities.filter(a => selectedItemIds.includes(a.id)).length;
      return {
        siteName,
        areaLabel: 'AMENITY',
        selectionLabel: 'Selected amenities',
        selectionLine: String(count),
        detailLabel: null,
        detailLine: null,
        total: preview?.total ?? count,
        toCreate: preview?.to_create,
        alreadyExists: preview?.already_exists,
      };
    }

    const count = streets.filter(s => selectedItemIds.includes(s.id)).length;
    return {
      siteName,
      areaLabel: 'STREETS',
      selectionLabel: 'Selected streets',
      selectionLine: String(count),
      detailLabel: null,
      detailLine: null,
      total: preview?.total ?? count,
      toCreate: preview?.to_create,
      alreadyExists: preview?.already_exists,
    };
  }, [
    sites,
    siteId,
    areaType,
    blocks,
    amenities,
    streets,
    selectedBlockIds,
    selectedFloors,
    selectedItemIds,
    preview,
  ]);

  const selectionTitle =
    areaType === 'blocks'
      ? 'Select blocks'
      : areaType === 'amenities'
        ? 'Select amenities'
        : 'Select streets';

  const canSubmit =
    selections.length > 0 && (preview?.to_create ?? summary.total) > 0;

  return (
    <form
      onSubmit={e => {
        e.preventDefault();
        onGenerate({
          site_id: siteId,
          selections,
          options: {
            skip_existing: true,
            default_landmark: defaultLandmark.trim(),
          },
        });
      }}
      className="flex flex-col flex-1 w-full min-h-0"
    >
      {error && (
        <div
          className="mx-5 sm:mx-6 mt-5 sm:mt-6 rounded-lg border border-red-300/50 bg-red-500/10 backdrop-blur-sm px-4 py-3 text-sm text-red-800 font-['Open_Sans']"
          role="alert"
        >
          {error}
        </div>
      )}

      <div className="flex flex-col flex-1 min-h-0">
        <div className="grid grid-cols-1 lg:grid-cols-[1fr_300px] flex-1 divide-y lg:divide-y-0 lg:divide-x divide-white/25 min-h-0">
          <div className="p-5 sm:p-6 flex flex-col gap-5">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <label htmlFor="qr-site" className={LABEL_CLASS}>
                  Site
                </label>
                <select
                  id="qr-site"
                  value={siteId}
                  onChange={handleSiteChange}
                  className={INPUT_CLASS}
                  disabled={isSaving || sites.length === 0}
                >
                  {sites.length === 0 ? (
                    <option value="">No sites available</option>
                  ) : (
                    sites.map(site => (
                      <option key={site.site_id} value={site.site_id}>
                        {site.site_name}
                      </option>
                    ))
                  )}
                </select>
              </div>
              <div>
                <span className={LABEL_CLASS}>Area type</span>
                <div
                  className="flex rounded-lg border border-white/30 bg-black/5 backdrop-blur-sm p-1"
                  role="tablist"
                  aria-label="Area type"
                >
                  {QR_AREA_TYPES.map(tab => (
                    <button
                      key={tab.id}
                      type="button"
                      role="tab"
                      aria-selected={areaType === tab.id}
                      className={`flex-1 rounded-md px-2 py-2 text-xs sm:text-sm font-semibold font-['Open_Sans'] transition-all ${
                        areaType === tab.id
                          ? 'bg-green-600 text-white shadow-md ring-2 ring-green-400/60'
                          : 'text-gray-700 hover:bg-white/10 hover:text-gray-900'
                      }`}
                      onClick={() => handleAreaTypeChange(tab.id)}
                      disabled={isSaving}
                    >
                      {tab.label}
                    </button>
                  ))}
                </div>
              </div>
            </div>

            <div>
              <label htmlFor="default-landmark" className={LABEL_CLASS}>
                Default landmark (optional)
              </label>
              <input
                id="default-landmark"
                type="text"
                value={defaultLandmark}
                onChange={e => setDefaultLandmark(e.target.value)}
                placeholder="e.g. Printed May 2026"
                className={INPUT_CLASS}
                disabled={isSaving}
              />
            </div>

            <div className="border-t border-white/25 pt-5">
              {areasLoading ? (
                <div className="flex justify-center py-8">
                  <div className="w-7 h-7 border-4 border-green-600 border-t-transparent rounded-full animate-spin" />
                </div>
              ) : areaType === 'blocks' ? (
                <div className="flex flex-col gap-4">
                  <div className="flex items-center justify-between gap-3">
                    <h4 className="text-sm font-bold text-gray-900 font-['Open_Sans']">
                      {selectionTitle}
                    </h4>
                    <SelectAllControl
                      checked={allBlocksSelected}
                      onChange={toggleAllBlocks}
                      disabled={isSaving || blocks.length === 0}
                    />
                  </div>
                  {blocks.length === 0 ? (
                    <p className="text-sm text-gray-500">
                      No blocks available for this site.
                    </p>
                  ) : (
                    <div className="flex flex-wrap gap-2">
                      {blocks.map(block => {
                        const selected = selectedBlockIds.includes(block.id);
                        return (
                          <button
                            key={block.id}
                            type="button"
                            onClick={() => toggleBlock(block.id)}
                            disabled={isSaving}
                            className={`px-4 py-2 rounded-lg border text-sm font-semibold font-['Open_Sans'] transition-colors ${
                              selected
                                ? 'border-green-600 bg-green-600 text-white shadow-sm'
                                : 'border-white/40 bg-white/10 backdrop-blur-sm text-gray-800 hover:border-green-400/60 hover:bg-white/15 hover:text-green-900'
                            }`}
                          >
                            {block.name}
                          </button>
                        );
                      })}
                    </div>
                  )}

                  {selectedBlockIds.map(blockId => {
                    const block = blocks.find(b => b.id === blockId);
                    if (!block) return null;
                    const selectedFloorNos = selectedFloors[block.id] || [];

                    return (
                      <div
                        key={block.id}
                        className="rounded-xl border border-white/30 bg-white/5 backdrop-blur-sm p-4"
                      >
                        <div className="flex items-center justify-between gap-3 mb-3">
                          <h5 className="text-sm font-bold text-gray-900 font-['Open_Sans']">
                            Floors ({block.name})
                          </h5>
                          <SelectAllControl
                            checked={allFloorsSelectedForBlock(block)}
                            onChange={() => toggleAllFloorsForBlock(block)}
                            disabled={isSaving}
                          />
                        </div>
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                          {(block.floors ?? []).map(floor => (
                            <CheckboxRow
                              key={`${block.id}-${floor.floor_no}`}
                              label={floor.name}
                              checked={selectedFloorNos.includes(floor.floor_no)}
                              onChange={() => toggleFloor(block.id, floor.floor_no)}
                              disabled={isSaving}
                            />
                          ))}
                        </div>
                      </div>
                    );
                  })}
                </div>
              ) : areaType === 'amenities' ? (
                amenities.length === 0 ? (
                  <p className="text-sm text-gray-500">
                    No amenities available for this site.
                  </p>
                ) : (
                  <SearchCheckboxList
                    searchId="amenity-search"
                    searchLabel="Search amenities"
                    searchValue={amenitySearch}
                    onSearchChange={setAmenitySearch}
                    items={filteredAmenities}
                    selectedIds={selectedItemIds}
                    onToggle={toggleItem}
                    onToggleAll={() =>
                      toggleAllFiltered(filteredAmenities, allFilteredAmenitiesSelected)
                    }
                    allSelected={allFilteredAmenitiesSelected}
                    isSaving={isSaving}
                    noResultsMessage={
                      amenitySearch
                        ? 'No amenities match your search.'
                        : 'No amenities available.'
                    }
                    columns={2}
                  />
                )
              ) : streets.length === 0 ? (
                <p className="text-sm text-gray-500">
                  No streets available for this site.
                </p>
              ) : (
                <SearchCheckboxList
                  searchId="street-search"
                  searchLabel="Search streets"
                  searchValue={streetSearch}
                  onSearchChange={setStreetSearch}
                  items={filteredStreets}
                  selectedIds={selectedItemIds}
                  onToggle={toggleItem}
                  onToggleAll={() =>
                    toggleAllFiltered(filteredStreets, allFilteredStreetsSelected)
                  }
                  allSelected={allFilteredStreetsSelected}
                  isSaving={isSaving}
                  noResultsMessage={
                    streetSearch
                      ? 'No streets match your search.'
                      : 'No streets available.'
                  }
                  columns={1}
                />
              )}
            </div>
          </div>

          <aside className="bg-gradient-to-b from-green-500/15 to-transparent p-5 sm:p-6 lg:min-h-[320px]">
            <h4 className="text-sm font-bold uppercase tracking-wider text-gray-900 font-['Open_Sans'] mb-5 pb-3 border-b border-green-500/25">
              Summary
            </h4>
            <dl className="flex flex-col gap-4 font-['Open_Sans']">
              <SummaryRow label="Site" value={summary.siteName} />
              <SummaryRow label="Area type" value={summary.areaLabel} />
              <SummaryRow
                label={summary.selectionLabel}
                value={summary.selectionLine}
              />
              {summary.detailLabel && (
                <SummaryRow label={summary.detailLabel} value={summary.detailLine} />
              )}
              {previewLoading && (
                <p className="text-xs text-gray-500">Updating preview…</p>
              )}
              {previewError && (
                <p className="text-xs text-amber-700">{previewError}</p>
              )}
              {summary.alreadyExists != null && (
                <SummaryRow
                  label="Already exist"
                  value={String(summary.alreadyExists)}
                />
              )}
              <div className="pt-3 mt-1 border-t border-green-500/25">
                <SummaryRow
                  label="QR codes to create"
                  value={
                    summary.toCreate != null
                      ? String(summary.toCreate)
                      : String(summary.total)
                  }
                  highlight
                />
              </div>
            </dl>
          </aside>
        </div>

        <div className="mt-auto flex flex-col-reverse sm:flex-row sm:items-center sm:justify-end gap-3 px-5 sm:px-6 py-4 bg-black/5 border-t border-white/25 backdrop-blur-sm">
          <button
            type="button"
            onClick={onCancel}
            disabled={isSaving}
            className="px-6 py-2.5 rounded-lg border border-white/40 bg-white/15 backdrop-blur-md text-sm font-semibold text-gray-800 font-['Open_Sans'] hover:bg-white/25 disabled:opacity-50 sm:min-w-[120px]"
          >
            Cancel
          </button>
          <button
            type="submit"
            disabled={isSaving || !canSubmit}
            className="px-8 py-2.5 rounded-lg bg-green-600 text-sm font-semibold text-white font-['Open_Sans'] hover:bg-green-700 disabled:opacity-50 shadow-sm sm:min-w-[180px]"
          >
            {isSaving ? 'Generating…' : 'Generate QR'}
          </button>
        </div>
      </div>
    </form>
  );
};

export default CreateMultipleQR;
